/**
 * Lightweight, module-scoped branding store.
 *
 * `Brand` is rendered on every page (sidebar + public layout) and we do NOT
 * want to fire an /api/landing request per mount. This module caches the
 * response for the lifetime of the tab, exposes a `useBranding()` hook, and
 * lets any updater (e.g. the admin Landing page's save handler) push a fresh
 * copy so every mounted `Brand` re-renders immediately.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

let _cache = null;          // last-known branding payload
let _inflight = null;       // dedupe concurrent fetches
const _listeners = new Set();

const _notify = () => { for (const cb of _listeners) cb(_cache); };

async function fetchOnce() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api.get("/landing")
    .then(({ data }) => { _cache = data || {}; return _cache; })
    .catch(() => { _cache = {}; return _cache; })
    .finally(() => { _inflight = null; });
  return _inflight;
}

/**
 * Push a fresh branding snapshot into the cache and notify subscribers.
 * Called by the admin Landing page after saving so the sidebar/public brand
 * updates without a full reload.
 */
export function setBranding(next) {
  _cache = { ..._cache, ...(next || {}) };
  _notify();
}

/**
 * React hook — returns the latest branding record (or an empty object while
 * the first fetch is in flight). Guaranteed to update in place when
 * `setBranding()` is invoked elsewhere.
 */
export function useBranding() {
  const [snap, setSnap] = useState(_cache || {});
  useEffect(() => {
    const listener = (next) => setSnap(next || {});
    _listeners.add(listener);
    // Kick off the initial fetch if we haven't yet.
    if (!_cache) {
      fetchOnce().then(_notify);
    } else {
      setSnap(_cache);
    }
    return () => { _listeners.delete(listener); };
  }, []);
  return snap;
}
